import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, CircularProgress, FormLabel, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import { useEditor, useEditorState, EditorContent, ReactRenderer } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Mention from '@tiptap/extension-mention';
import Placeholder from '@tiptap/extension-placeholder';
import tippy, { type Instance as TippyInstance } from 'tippy.js';
import 'tippy.js/dist/tippy.css';
import { BulletinRed, BulletinBlue, BulletinBlack } from '../extensions/bulletinTipTapMarks';
import { bulletinStringToTipTapJSON, tipTapJSONToBulletinString } from '../utils/bulletinTipTapBridge';
import { apiClient } from '../api/client';
import type { PatientAssignee } from '../constants/patient';
import BulletinMentionList from './BulletinMentionList';

type Props = {
  label: string;
  value: string;
  onChange: (next: string) => void;
  /** 最低の高さ（px 相当） */
  minRows?: number;
  helperText?: string;
  disabled?: boolean;
  error?: boolean;
};

/** 行数に近い最小高さ（1 行 ≒ 24px + padding） */
function minHeightFromRows(rows: number): number {
  return Math.max(120, rows * 24 + 32);
}

const BulletinRichTextEditor: React.FC<Props> = ({
  label,
  value,
  onChange,
  minRows = 8,
  helperText,
  disabled = false,
  error = false,
}) => {
  const [staff, setStaff] = useState<PatientAssignee[]>([]);
  const staffRef = useRef<PatientAssignee[]>([]);
  const skipSyncFromParent = useRef(false);
  /** 親の value をエディタに反映するまで onUpdate で onChange しない（初回の空ドキュメントで親 state を空にしない） */
  const syncedFromParentRef = useRef(false);
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    staffRef.current = staff;
  }, [staff]);

  useEffect(() => {
    void apiClient
      .get<PatientAssignee[]>('/api/staff-directory')
      .then((r) => setStaff(r.data))
      .catch(() => {
        /* 候補なしで続行 */
      });
  }, []);

  const extensions = useMemo(
    () => [
      StarterKit.configure({
        blockquote: false,
        bulletList: false,
        orderedList: false,
        codeBlock: false,
        horizontalRule: false,
        heading: false,
        strike: false,
        italic: false,
        code: false,
      }),
      BulletinRed,
      BulletinBlue,
      BulletinBlack,
      Placeholder.configure({
        placeholder: '本文を入力…',
      }),
      Mention.configure({
        deleteTriggerWithBackspace: true,
        HTMLAttributes: { class: 'bulletin-mention-chip' },
        suggestion: {
          char: '@',
          allowSpaces: false,
          items: ({ query }) => {
            const q = query.trim().toLowerCase();
            return staffRef.current
              .filter((s) => !q || s.display_name.toLowerCase().includes(q))
              .slice(0, 30)
              .map((s) => ({ id: String(s.id), label: s.display_name }));
          },
          render: () => {
            let component: ReactRenderer | null = null;
            let popup: TippyInstance | null = null;

            return {
              onStart: (props) => {
                component = new ReactRenderer(BulletinMentionList, {
                  editor: props.editor,
                  props: {
                    items: props.items,
                    command: (item: { id: string; label: string }) => {
                      props.command(item);
                    },
                  },
                });
                if (!props.clientRect) {
                  return;
                }
                popup = tippy(document.body, {
                  getReferenceClientRect: () => props.clientRect?.() ?? new DOMRect(),
                  appendTo: () => document.body,
                  content: component.element,
                  showOnCreate: true,
                  interactive: true,
                  trigger: 'manual',
                  placement: 'bottom-start',
                  zIndex: 1300,
                });
              },
              onUpdate(props) {
                component?.updateProps({
                  items: props.items,
                  command: (item: { id: string; label: string }) => {
                    props.command(item);
                  },
                });
                popup?.setProps({
                  getReferenceClientRect: () => props.clientRect?.() ?? new DOMRect(),
                });
              },
              onExit() {
                popup?.destroy();
                component?.destroy();
                popup = null;
                component = null;
              },
              onKeyDown(props) {
                if (props.event.key === 'Escape') {
                  popup?.destroy();
                  component?.destroy();
                  popup = null;
                  component = null;
                  return true;
                }
                const handler = component?.ref as { onKeyDown?: (e: KeyboardEvent) => boolean } | null;
                return handler?.onKeyDown?.(props.event) ?? false;
              },
            };
          },
        },
      }),
    ],
    []
  );

  const editor = useEditor(
    {
      immediatelyRender: false,
      extensions,
      editorProps: {
        attributes: {
          class: 'bulletin-rich-text-root',
        },
      },
      onUpdate: ({ editor: ed }) => {
        if (skipSyncFromParent.current) return;
        if (!syncedFromParentRef.current) return;
        onChangeRef.current(tipTapJSONToBulletinString(ed.getJSON()));
      },
    },
    [extensions]
  );

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [disabled, editor]);

  useEffect(() => {
    if (!editor) {
      syncedFromParentRef.current = false;
      return;
    }
    const current = tipTapJSONToBulletinString(editor.getJSON());
    if (current === value) {
      syncedFromParentRef.current = true;
      return;
    }
    skipSyncFromParent.current = true;
    editor.commands.setContent(bulletinStringToTipTapJSON(value), { emitUpdate: false });
    skipSyncFromParent.current = false;
    syncedFromParentRef.current = true;
  }, [value, editor]);

  const runBold = useCallback(() => {
    editor?.chain().focus().toggleBold().run();
  }, [editor]);
  const runRed = useCallback(() => {
    editor?.chain().focus().toggleMark('bulletinRed').run();
  }, [editor]);
  const runBlue = useCallback(() => {
    editor?.chain().focus().toggleMark('bulletinBlue').run();
  }, [editor]);
  const runBlack = useCallback(() => {
    editor?.chain().focus().toggleMark('bulletinBlack').run();
  }, [editor]);

  const toolbarActive = useEditorState({
    editor,
    selector: ({ editor: ed }) => {
      if (!ed) {
        return { boldActive: false, activeColor: null as 'red' | 'blue' | 'black' | null };
      }
      const boldActive = ed.isActive('bold');
      const isRed = ed.isActive('bulletinRed');
      const isBlue = ed.isActive('bulletinBlue');
      const isBlack = ed.isActive('bulletinBlack');
      // 表示上は色を排他（スキーマでも excludes 済み。万一複数なら赤>青>黒の優先）
      let activeColor: 'red' | 'blue' | 'black' | null = null;
      if (isRed) activeColor = 'red';
      else if (isBlue) activeColor = 'blue';
      else if (isBlack) activeColor = 'black';
      return { boldActive, activeColor };
    },
  });

  const boldActive = toolbarActive?.boldActive ?? false;
  const activeColor = toolbarActive?.activeColor ?? null;

  const minH = minHeightFromRows(minRows);

  if (!editor) {
    return (
      <Box sx={{ mb: 2 }}>
        <FormLabel
          error={error}
          sx={{
            display: 'block',
            mb: 0.5,
            typography: 'body2',
            color: error ? 'error.main' : 'text.secondary',
          }}
        >
          {label}
        </FormLabel>
        <Box
          sx={{
            border: 1,
            borderColor: error ? 'error.main' : 'divider',
            borderRadius: 1,
            minHeight: minH,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress size={28} />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 2 }}>
      <FormLabel
        error={error}
        sx={{
          display: 'block',
          mb: 0.5,
          typography: 'body2',
          color: error ? 'error.main' : 'text.secondary',
          '&.Mui-focused': { color: error ? 'error.main' : 'primary.main' },
        }}
      >
        {label}
      </FormLabel>
      <Box
        sx={{
          border: 1,
          borderColor: error ? 'error.main' : 'divider',
          borderRadius: 1,
          overflow: 'hidden',
          bgcolor: 'background.paper',
          '& .bulletin-rich-text-root': {
            outline: 'none',
            minHeight: minH,
            px: 1.5,
            py: 1,
            typography: 'body1',
            '& p': { margin: 0, minHeight: '1.4em' },
            '& p + p': { marginTop: '0.35em' },
            '& .bulletin-mention-chip': {
              display: 'inline',
              color: 'primary.main',
              fontWeight: 600,
              backgroundColor: (t) => (t.palette.mode === 'dark' ? 'rgba(144,202,249,0.12)' : 'rgba(25,118,210,0.08)'),
              borderRadius: 0.5,
              px: 0.25,
            },
          },
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, px: 1, pt: 1, pb: 0.5, borderBottom: 1, borderColor: 'divider' }}>
          <Button
            size="small"
            variant="outlined"
            color="primary"
            onMouseDown={(e) => e.preventDefault()}
            onClick={runBold}
            disabled={disabled || !editor}
            sx={(t) => ({
              minWidth: 36,
              px: 0.75,
              ...(boldActive && {
                bgcolor: alpha(t.palette.primary.main, t.palette.mode === 'dark' ? 0.22 : 0.12),
                borderColor: t.palette.primary.main,
                color: t.palette.primary.main,
              }),
            })}
            aria-label="太字"
            aria-pressed={boldActive}
          >
            <FormatBoldIcon fontSize="small" />
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            onMouseDown={(e) => e.preventDefault()}
            onClick={runRed}
            disabled={disabled || !editor}
            sx={(t) => ({
              minWidth: 36,
              px: 0.75,
              ...(activeColor === 'red' && {
                bgcolor: alpha(t.palette.error.main, t.palette.mode === 'dark' ? 0.22 : 0.12),
                borderColor: t.palette.error.main,
                color: t.palette.error.main,
              }),
            })}
            aria-label="赤字"
            aria-pressed={activeColor === 'red'}
          >
            赤
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="info"
            onMouseDown={(e) => e.preventDefault()}
            onClick={runBlue}
            disabled={disabled || !editor}
            sx={(t) => ({
              minWidth: 36,
              px: 0.75,
              ...(activeColor === 'blue' && {
                bgcolor: alpha(t.palette.info.main, t.palette.mode === 'dark' ? 0.22 : 0.12),
                borderColor: t.palette.info.main,
                color: t.palette.info.main,
              }),
            })}
            aria-label="青字"
            aria-pressed={activeColor === 'blue'}
          >
            青
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            onMouseDown={(e) => e.preventDefault()}
            onClick={runBlack}
            disabled={disabled || !editor}
            sx={(t) => ({
              minWidth: 36,
              px: 0.75,
              color: 'text.primary',
              borderColor: 'text.secondary',
              ...(activeColor === 'black' && {
                bgcolor: alpha(t.palette.text.primary, t.palette.mode === 'dark' ? 0.14 : 0.08),
                borderColor: t.palette.text.secondary,
                color: t.palette.text.primary,
              }),
            })}
            aria-label="黒字（通常色）"
            aria-pressed={activeColor === 'black'}
          >
            黒
          </Button>
        </Box>
        <EditorContent editor={editor} />
      </Box>
      {helperText && (
        <Typography variant="caption" color={error ? 'error' : 'text.secondary'} sx={{ mt: 0.5, display: 'block' }}>
          {helperText}
        </Typography>
      )}
    </Box>
  );
};

export default BulletinRichTextEditor;
